import * as React from "react";
import { RouteComponentProps, useHistory, useParams } from "react-router-dom";
import { useSecurityContext } from "../../../Core/SecurityContext";
import { PermissionEnum } from "../../../misc/api/ApiPermissionEnum";
import { PressureAnalysisSetTypeEnum } from "../../../misc/api/ApiPressureAnalysisSetTypeEnum";
import { SessionTypes } from "../../../misc/api/ApiSessionTypes";
import { FixedCommentMutation, OverreadChallengeStatusEnum, OverreadStatusEnum, OverreadStatusIdEnum, Permission, PressureFlow, PressureTestFlow, SessionMetadata, TestAcceptabilityMutation } from "../../../misc/api/apiTypes";
import Alert, { AlertCategoryEnum } from "../../../misc/components/Alert";
import AcceptabilityDashboard from "../../Shared/AcceptabilityDashboard";
import NotesPanel from "../../Shared/NotesPanel";
import { PFTResults, PressureResults, StudyRelevantParameters } from "../../Shared/PFTAPITypes";
import SessionMetadataTable from "../../Shared/SessionMetadataTable";
import { TestQualityGrade } from "../../Shared/TestQualityGrading";
import { DoneLoadingBestValuesAction, LoadingBestValuesAction, UpdateBestTestAction, UpdateRepeatabilityAction, useAcceptability } from "../../Shared/UseAcceptability";
import { ToggleSessionFixedCommentsVisibilityAction, useFixedComments } from "../../Shared/UseFixedComments";
import { MepResultsTable } from "./MepResultsTable";
import LoadingWithMessage from "../../../misc/components/LoadingWithMessage";
import { PressureGraphs } from "../PressureGraphs";
import { usePressureGraph } from "../UsePressureGraph";
import { MepAnalysisSet, MepSession, SessionNote, WebPortalClient } from "webportalclient";
import { ChallengeAlert } from '../../Shared/ChallengeAlert';
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../../misc/api/WebPortalTokenClient";
import { GoBackToList } from "../../Shared/AnalysisHelpers";
import { FixedCommentSetTypeEnum } from "../../../misc/api/FixedComments/FixedCommentSetTypes";
import { LoadAnalysisSetSelectionsAction, useNextGenFixedComments } from "../../Spirometry/Testing/UseNextGenFixedComments";
import { OverreadingAcceptabilityDashboard } from "../../Shared/OverreadingAcceptabilityDashboard";
import { useCurrentStudyConfigurationContext } from "../../../Core/CurrentStudyConfigurationContext";
import { FixedCommentEntityTypeEnum } from "../../../misc/api/FixedComments/FixedCommentSetEntityTypes";

enum ViewState {
    View = "view",
    Overread = "overread"
}

interface ExpectedMatchParams {
    analysisSetId: string;
    viewState: ViewState;
}

export interface MepAnalysisScreenProps {
    currentPermissions: Permission[];
}

export interface MepAnalysisScreenRouteProps extends RouteComponentProps<ExpectedMatchParams>, MepAnalysisScreenProps { }

export const MepAnalysisScreen = () => {
    const { analysisSetId, viewState } = useParams<ExpectedMatchParams>();
    const history = useHistory();
    const [analysisSet, setAnalysisSet] = React.useState<PFTResults>(null);
    const [overreadAnalysisSet, setOverreadAnalysisSet] = React.useState<MepAnalysisSet>();
    const [siteAnalysisSet, setSiteAnalysisSet] = React.useState<MepAnalysisSet>();
    const [mepSession, setMepSession] = React.useState<MepSession>();
    const [sessionMetadata, setSessionMetadata] = React.useState<SessionMetadata>(null);
    const [flowData, setFlowData] = React.useState<PressureFlow>(null);
    const [isViewOnly, setIsViewOnly] = React.useState<boolean>(true);
    const [isAnalysisRejected, setIsAnalysisRejected] = React.useState<boolean>(false);
    const [shouldValidateFixedComments, setShouldValidateFixedComments] = React.useState(false);
    const [sessionNotes, setSessionNotes] = React.useState<SessionNote[]>(null);
    const [studyRelevantParameters, setStudyRelevantParameters] = React.useState<StudyRelevantParameters>(null);
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | undefined>();
    const [noteError, setNoteError] = React.useState<string | undefined>();
    const [graphState, graphDispatch] = usePressureGraph(flowData);
    const [fixedCommentStateTechnician, fixedCommentDispatchTechnician] = useNextGenFixedComments(SessionTypes.Mep, FixedCommentSetTypeEnum.Technician, siteAnalysisSet);
    const [fixedCommentStateOverreading, fixedCommentDispatchOverreading] = useNextGenFixedComments(SessionTypes.Mep, FixedCommentSetTypeEnum.Overread, overreadAnalysisSet);
    const { currentUserPermissions } = useSecurityContext();
    const [acceptabilityState, acceptabilityDispatch] = useAcceptability(analysisSet);
    const client = useWebPortalApiClient();
    const legacyClient = useWebPortalLegacyClient();

    React.useEffect(() => {
        const fetchAnalysisSet = async () => {
            const access = await client.client.getMepAnalysisSet(analysisSetId, "canEdit");

            setIsViewOnly(!access.canEdit);
            const overreadPermission = currentUserPermissions.find(p => p.Id == PermissionEnum.OverreadMepSession);
            setIsViewOnly(!overreadPermission);

            if (access.canEdit && viewState === ViewState.Overread && overreadPermission) {
                setIsViewOnly(false);
                await legacyClient.startMepOverreading(analysisSetId);
            } else {
                setIsViewOnly(true);
            }

            const results = await legacyClient.getMepAnalysisSetResults(analysisSetId);
            setAnalysisSet(results);

            const sessionMetadataResponse = await legacyClient.getMepSession(results.SessionId);
            setSessionMetadata(sessionMetadataResponse);

            const currentAnalysisSet = sessionMetadataResponse.AnalysisSets.find(a => a.Id === analysisSetId);
            const isQc = currentAnalysisSet.Type.Id === PressureAnalysisSetTypeEnum.QC;

            const overreadAnalysisSetId = viewState === ViewState.Overread || isQc ? analysisSetId : sessionMetadataResponse.AnalysisSets.find(a => a.Type.Id === PressureAnalysisSetTypeEnum.Overread).Id;
            const overreadAnalysisSetResult = await client.client.getMepAnalysisSet(overreadAnalysisSetId, undefined, "tests($expand=test)");
            setOverreadAnalysisSet(overreadAnalysisSetResult);

            const siteAnalysisSetId = sessionMetadataResponse.AnalysisSets.find(a => a.Type.Id === PressureAnalysisSetTypeEnum.Site).Id;
            const siteAnalysisSetResult = await client.client.getMepAnalysisSet(siteAnalysisSetId, undefined, "tests");
            setSiteAnalysisSet(siteAnalysisSetResult);

            const mepSessionResult = await client.client.getMepSession(sessionMetadataResponse.Id, undefined, "tests");
            setMepSession(mepSessionResult);

            fixedCommentDispatchOverreading(LoadAnalysisSetSelectionsAction.create(overreadAnalysisSetResult.fixedComments, overreadAnalysisSetResult.tests));
            fixedCommentDispatchTechnician(LoadAnalysisSetSelectionsAction.create(siteAnalysisSetResult.fixedComments, siteAnalysisSetResult.tests));

            const mepFlow = await legacyClient.getMepFlowData(analysisSetId);
            setFlowData(mepFlow);

            const parameters = await legacyClient.getStudyMepRelevantParameters();
            setStudyRelevantParameters(parameters);

            const isRejected = sessionMetadataResponse.AnalysisSets.find(a => a.Id === analysisSetId).Status.Id === OverreadStatusIdEnum.Rejected;
            setIsAnalysisRejected(isRejected);

            await fetchSessionNotes(results.SessionId);
        }

        fetchAnalysisSet();
    }, [analysisSetId, viewState]);

    React.useEffect(() => {

        const updateBestTestAndRepeatability = async () => {
            if (acceptabilityState.spirometryResults.TestResults.length) {

                acceptabilityDispatch(LoadingBestValuesAction.create());

                const repeatabilityValues = await legacyClient.calculateMepRepeatability(sessionMetadata.Id, acceptabilityState.spirometryResults.TestResults);
                const bestTestValues = await legacyClient.calculateMepBestTests(acceptabilityState.spirometryResults.TestResults);

                acceptabilityDispatch(UpdateBestTestAction.create(bestTestValues));
                acceptabilityDispatch(UpdateRepeatabilityAction.create(repeatabilityValues));

                acceptabilityDispatch(DoneLoadingBestValuesAction.create());
            }
        }
        updateBestTestAndRepeatability();
    }, [acceptabilityState.changedAcceptability, acceptabilityDispatch]);

    const fetchSessionNotes = async (sessionId: string) => {
        const notes = await client.client.getSessionNotes(undefined, `sessionId eq ${sessionId}`);
        setSessionNotes(notes.value);
    }

    const addNewNote = async (annotation: string) => {
        try {
            await legacyClient.putSessionNotes(analysisSet.SessionId, annotation);
        } catch (ex) {
            setNoteError(ex.Message);
            return;
        }
        await fetchSessionNotes(analysisSet.SessionId);
    }

    const editNote = async (id: string, comment: string, annotation: string) => {
        await client.client.patchSessionNote(
            {
                comment: comment,
            }, id, annotation);
        await fetchSessionNotes(analysisSet.SessionId);
    }

    const setAnalysisDecision = async (annotation: string, decision: OverreadStatusEnum) => {
        setIsSubmitting(true);
        setError(undefined);

        const sessionFixedCommentsMutation = fixedCommentStateOverreading.sessionFixedComments.comments.map(x => ({ CommentId: x.id, IsSelected: x.selected } as FixedCommentMutation));
        const testFixedCommentsMutation = fixedCommentStateOverreading.testFixedComments.map(x => ({
            TestId: x.testId, Comments: x.comments.map(c => ({ CommentId: c.id, IsSelected: c.selected } as FixedCommentMutation)),
        }));
        const testAcceptability = acceptabilityState.spirometryResults.TestResults.map(x => ({ TestId: x.TestId, Accepted: x.OverreaderAcceptability ? true : false } as TestAcceptabilityMutation));

        try {
            const response = await legacyClient.updateMepAcceptability(analysisSetId, decision, testAcceptability, sessionFixedCommentsMutation, testFixedCommentsMutation, acceptabilityState.spirometryResults.TestResults, annotation);

            if (response.AnalysisSetId !== analysisSetId) {
                setError("An error occurred saving the analysis set. Please contact an Administrator.");
            }

            GoBackToList(history, currentUserPermissions, "/Mep", PermissionEnum.OverreadMepSession, "/Mep/Analysis");
        }
        catch (ex) {
            setError(ex.Message);
            window.scrollTo(0, 0);
        }
    }

    const graphData: PressureTestFlow[] = React.useMemo(() => {
        if (!(graphState.flow && sessionMetadata && analysisSet && fixedCommentStateOverreading.areAvailableFixedCommentsLoaded !== null))
            return;

        return graphState.flow.Tests.map(vt => {
            return {
                TestId: vt.TestId,
                TestNumber: vt.TestNumber,
                ColorHex: vt.ColorHex,
                IsDisplayed: vt.IsDisplayed,
                PressureData: vt.PressureData,
            }
        })
    }, [graphState.flow, sessionMetadata, analysisSet, fixedCommentStateOverreading.areAvailableFixedCommentsLoaded]);

    if (analysisSet === null || sessionMetadata === null || studyRelevantParameters === null || sessionNotes == null) {
        return (<LoadingWithMessage message="Loading MEP Data..." />);
    }

    const overreadingDisabled = viewState === "overread" && (fixedCommentStateOverreading.areAvailableFixedCommentsLoaded === false || (fixedCommentStateOverreading.areFixedCommentsLoaded === undefined || fixedCommentStateOverreading.areFixedCommentsLoaded === null || fixedCommentStateOverreading.areFixedCommentsLoaded === false));
    const hasAddingNotePermission = currentUserPermissions.some(x => x.Id == PermissionEnum.AddMepSessionNotes);
    const hasViewNotePermission = currentUserPermissions.some(x => x.Id == PermissionEnum.ViewMepSessionNotes);

    const isQcAnalysisSet = sessionMetadata.AnalysisSets.find(x => x.Id === analysisSetId).Type.Id === PressureAnalysisSetTypeEnum.QC;
    const isSiteAnalysisSet = sessionMetadata.AnalysisSets.find(x => x.Id === analysisSetId).Type.Id === PressureAnalysisSetTypeEnum.Site;
    const isOverreadAnalysisSet = sessionMetadata.AnalysisSets.find(x => x.Id === analysisSetId).Type.Id === PressureAnalysisSetTypeEnum.Overread;

    return (
        <>
            <Alert show={overreadingDisabled} category={AlertCategoryEnum.Danger}>
                Over-reading disabled. Fixed comments have not been configured for this study, please ask the Administrator to configure these first.
            </Alert>

            <Alert show={error !== undefined} category={AlertCategoryEnum.Danger}>
                {error}
            </Alert>

            {!isQcAnalysisSet && !isSiteAnalysisSet && !isViewOnly && sessionMetadata.ChallengeStatusId === OverreadChallengeStatusEnum.Challenged && !sessionMetadata.ChallengeIsClosed ?
                <ChallengeAlert
                    sessionId={sessionMetadata.Id}
                    sessionType={SessionTypes.Mep}
                    onRejectComplete={() => GoBackToList(history, currentUserPermissions, "/Mep", PermissionEnum.OverreadMepSession, "/Mep/Analysis")}
                /> : null}

            <h2 className="text-primary">MEP Data</h2>

            <SessionMetadataTable sessionMetadata={sessionMetadata} selectedAnalysisSetId={analysisSetId} isQc={isQcAnalysisSet} />

            <h2 className="text-primary">Charts</h2>
            <div className="panel panel-default">
                <div className="panel-body">
                    <PressureGraphs type="MEP" data={graphData} />
                </div>
            </div>

            <h2 className="text-primary">Results</h2>
            <div className="panel panel-default">
                <div className="panel-body">
                    <MepResultsTable
                        isRejected={isAnalysisRejected}
                        isViewOnly={isViewOnly || overreadingDisabled || acceptabilityState.loadingBestValues}
                        isSiteAnalysisSet={isSiteAnalysisSet}
                        resultData={acceptabilityState.spirometryResults as PressureResults}
                        session={mepSession}
                        fixedCommentsStateTechnician={fixedCommentStateTechnician}
                        fixedCommentsDispatchTechnician={fixedCommentDispatchTechnician}
                        fixedCommentsStateOverreading={fixedCommentStateOverreading}
                        fixedCommentsDispatchOverreading={fixedCommentDispatchOverreading}
                        shouldValidateFixedComments={shouldValidateFixedComments}
                        acceptabilityState={acceptabilityState}
                        acceptabilityDispatch={acceptabilityDispatch}
                        studyMepRelevantParameters={studyRelevantParameters}
                        graphState={graphState}
                        graphDispatch={graphDispatch}
                    />
                    {
                        hasViewNotePermission ?
                            <NotesPanel
                                header="Session Notes"
                                showNotesCount={true}
                                isAddingNotesEnabled={!isViewOnly && !overreadingDisabled && hasAddingNotePermission && isOverreadAnalysisSet}
                                notes={sessionNotes}
                                errorStatus={noteError}
                                onAddNote={(annotation) => addNewNote(annotation)}
                                onEditNote={(id,comment, annotation) => editNote(id,comment, annotation)}
                            />
                            : null
                    }

                    <AcceptabilityDashboard
                        isViewOnly={isViewOnly || overreadingDisabled}
                        analysisSetId={analysisSetId}
                        fixedCommentsDispatch={fixedCommentDispatchOverreading}
                        fixedCommentState={fixedCommentStateOverreading}
                        acceptabilityState={acceptabilityState}
                        sessionType={SessionTypes.Mep}
                        overreadStatus={sessionMetadata.AnalysisSets.find(a => a.Id === analysisSetId).Status.Id}
                        onNoSessionCommentsSelected={() => {
                            fixedCommentDispatchOverreading(ToggleSessionFixedCommentsVisibilityAction.create());
                        }}
                        onAcceptabilitySelected={(annotation, decision) => setAnalysisDecision(annotation, decision)}
                        onCancel={() => GoBackToList(history, currentUserPermissions, "/Mep", PermissionEnum.OverreadMepSession, "/Mep/Analysis")}
                    />
                </div>
            </div>
        </>
    );
}

export default MepAnalysisScreen;
